import isEmpty from "lodash-es/isEmpty"
import lowerCase from "lodash-es/lowerCase"

const ASSISTANT_ROOT_PATH: string = "/ea"

const PARTICIPANT_ROOT_PATH: string = "/p"

const COACH_OR_PM_ROOT_PATH: string = "/c-pm"

const PARTICIPANT_ONBOARDING_ROOT_PATH: string = `${PARTICIPANT_ROOT_PATH}/onboarding`

const ECoachTab: {
    Dashboard: { Sessions: string; Participants: string }
    Group: { Summary: string; Sessions: string }
    GroupParticipant: { Modules: string; Diagnostic: string; Sessions: string }
    IndividualParticipant: { Plan: string; Diagnostic: string; Sessions: string }
} = {
    Dashboard: {
        Sessions: "sessions",
        Participants: "participants"
    },
    Group: {
        Summary: "summary",
        Sessions: "sessions"
    },
    GroupParticipant: {
        Modules: "modules",
        Diagnostic: "diagnostic",
        Sessions: "sessions"
    },
    IndividualParticipant: {
        Plan: "plan",
        Diagnostic: "diagnostic",
        Sessions: "sessions"
    }
} as const

enum ERoute {
    Register = "/register/:key/",
    Login = "/login",
    ResetPassword = "/reset-password/:key/",
    //
    Participant_Dashboard = "/dashboard",
    Participant_DashboardModule = "/dashboard/:moduleId",
    Participant_Chapter = "/chapter",
    Participant_ChapterId = "/chapter/:chapterId",
    Participant_CoachingSessions = "/coaching-sessions",
    Participant_OnboardingProfile = "/p/onboarding/profile",
    Participant_OnboardingVideo = "/p/onboarding/video",
    Participant_OnboardingModules_Legacy = "/p/onboarding/modules",
    Participant_OnboardingDiagnostic = "/p/onboarding/diagnostic",
    Participant_OnboardingDiagnosticResults_Legacy = "/p/onboarding/diagnostic-results",
    Participant_OnboardingReflection = "/p/onboarding/reflection",
    Participant_OnboardingHolds = "/p/onboarding/holds",
    Participant_OnboardingCoach = "/p/onboarding/coach",
    Participant_OnboardingRedirect = "/p/onboarding/redirect",
    Participant_DiagnosticSummary = "/p/diagnostic-summary",
    //
    Coach_Dashboard = "/c-pm/dashboard",
    Coach_Group = "/c-pm/group/:groupId",
    Coach_GroupParticipant = "/c-pm/group/:groupId/participant/:participantId",
    Coach_IndividualParticipant = "/c-pm/participant/:participantId",
    Coach_RelinkAccounts = "/c-pm/relink-accounts",
    Coach_CalendarSetup = "/c-pm/calendar-setup",
    Coach_CalendarAccounts = "/c-pm/calendar-accounts",
    Coach_CalendarAvailability = "/c-pm/calendar-availability",
    //
    Assistant_Participants = "/ea/participants",
    Assistant_ParticipantSessions = "/ea/participants/:id/coaching-sessions",
    Assistant_SwapSession = "/ea/participants/:id/swap-session"
}

const EPath: {
    Register(key: string): string
    Login(opts: { isErrorReset: boolean }): string
    ResetPassword(key: string): string
    //
    Participant_Dashboard(): string
    Participant_DashboardModule(id: string | number): string
    Participant_Chapter(): string
    Participant_ChapterId(id: number, params?: string): string
    Participant_CoachingSessions(): string
    Participant_OnboardingProfile(): string
    Participant_OnboardingVideo(): string
    Participant_OnboardingModules_Legacy(): string
    Participant_OnboardingDiagnostic(): string
    Participant_OnboardingDiagnosticResults_Legacy(): string
    Participant_OnboardingReflection(): string
    Participant_OnboardingHolds(): string
    Participant_OnboardingCoach(): string
    Participant_OnboardingRedirect(): string
    Participant_DiagnosticSummary(): string
    //
    Coach_Dashboard(opts: { tab: keyof typeof ECoachTab.Dashboard }): string
    Coach_Group(params: { groupId: number }, opts: { tab: keyof typeof ECoachTab.Group; sessionId?: number }): string
    Coach_GroupParticipant(
        params: { groupId: number; participantId: number },
        opts: { tab: keyof typeof ECoachTab.GroupParticipant; moduleId?: number }
    ): string
    Coach_IndividualParticipant(
        params: { participantId: number },
        opts: { tab: keyof typeof ECoachTab.IndividualParticipant }
    ): string
    Coach_CalendarAccounts(): string
    Coach_RelinkAccounts(): string
    Coach_CalendarSetup(): string
    Coach_CalendarAvailability(): string
    //
    Assistant_Participants(): string
    Assistant_ParticipantSessions(id: number): string
    Assistant_SwapSession(userId: number, sessionId: number): string
} = {
    Register: (key: string): string => `${ERoute.Register}/${key}`,
    Login: (opts: { isErrorReset: boolean }): string => `${ERoute.Login}${opts.isErrorReset ? "?isError=true" : ""}`,
    ResetPassword: (key: string): string => `${ERoute.ResetPassword}/${key}`,
    //
    Participant_Dashboard: (): string => ERoute.Participant_Dashboard,
    Participant_DashboardModule: (id: string | number): string => `${ERoute.Participant_Dashboard}/${id}`,
    Participant_Chapter: (): string => ERoute.Participant_Chapter,
    Participant_ChapterId: (id: number, params?: string): string =>
        isEmpty(params) ? `${ERoute.Participant_Chapter}/${id}` : `${ERoute.Participant_Chapter}/${id}?${params}`,
    Participant_CoachingSessions: (): string => ERoute.Participant_CoachingSessions,
    Participant_OnboardingProfile: (): string => ERoute.Participant_OnboardingProfile,
    Participant_OnboardingVideo: (): string => ERoute.Participant_OnboardingVideo,
    Participant_OnboardingModules_Legacy: (): string => ERoute.Participant_OnboardingModules_Legacy,
    Participant_OnboardingDiagnostic: (): string => ERoute.Participant_OnboardingDiagnostic,
    Participant_OnboardingDiagnosticResults_Legacy: (): string => ERoute.Participant_OnboardingDiagnosticResults_Legacy,
    Participant_OnboardingReflection: (): string => ERoute.Participant_OnboardingReflection,
    Participant_OnboardingHolds: (): string => ERoute.Participant_OnboardingHolds,
    Participant_OnboardingCoach: (): string => ERoute.Participant_OnboardingCoach,
    Participant_OnboardingRedirect: (): string => ERoute.Participant_OnboardingRedirect,
    Participant_DiagnosticSummary: (): string => ERoute.Participant_DiagnosticSummary,
    //
    Coach_Dashboard: (opts: { tab: keyof typeof ECoachTab.Dashboard }): string =>
        `${ERoute.Coach_Dashboard}?tab=${lowerCase(opts.tab)}`,
    Coach_Group: (p: { groupId: number }, opts: { tab: keyof typeof ECoachTab.Group; sessionId?: number }): string =>
        `${COACH_OR_PM_ROOT_PATH}/group/${p.groupId}?tab=${lowerCase(opts.tab)}${opts?.sessionId ? `&sessionId=${opts.sessionId}` : ""}`,
    Coach_GroupParticipant: (
        p: { groupId: number; participantId: number },
        opts: { tab?: keyof typeof ECoachTab.GroupParticipant; moduleId?: number }
    ): string =>
        `${COACH_OR_PM_ROOT_PATH}/group/${p.groupId}/participant/${p.participantId}?tab=${lowerCase(opts.tab)}${opts?.moduleId ? `&moduleId=${opts.moduleId}` : ""}`,
    Coach_IndividualParticipant: (
        p: { participantId: number },
        opts: { tab: keyof typeof ECoachTab.IndividualParticipant }
    ): string => `${COACH_OR_PM_ROOT_PATH}/participant/${p.participantId}?tab=${lowerCase(opts.tab)}`,
    Coach_CalendarAccounts: (): string => ERoute.Coach_CalendarAccounts,
    Coach_RelinkAccounts: (): string => ERoute.Coach_RelinkAccounts,
    Coach_CalendarSetup: (): string => ERoute.Coach_CalendarSetup,
    Coach_CalendarAvailability: (): string => ERoute.Coach_CalendarAvailability,
    //
    Assistant_Participants: (): string => ERoute.Assistant_Participants,
    Assistant_ParticipantSessions: (id: number): string =>
        `${ASSISTANT_ROOT_PATH}/participants/${id}/coaching-sessions`,
    Assistant_SwapSession: (id: number, sessionId: number): string =>
        `${ASSISTANT_ROOT_PATH}/participants/${id}/swap-session?sessionId=${sessionId}`
} as const

export {
    ASSISTANT_ROOT_PATH,
    COACH_OR_PM_ROOT_PATH,
    PARTICIPANT_ROOT_PATH,
    PARTICIPANT_ONBOARDING_ROOT_PATH,
    ERoute,
    EPath,
    ECoachTab
}
